import React from 'react'
import type { StandingRowOut, FormTeamOut, FixtureLite, TeamLite } from '../../types'

interface LeagueTableProps {
  standings: StandingRowOut[]
  allTeamsForm?: Record<number, FormTeamOut[]>
  nextFixtures?: FixtureLite[]
  onTeamClick?: (team: TeamLite) => void
  onFixtureClick?: (fixtureId: number) => void
  highlightTeamId?: number
  compact?: boolean
}

// Render form badges (W/D/L colored boxes) - FotMob style
function FormBadges({
  form,
  formDetails,
  onFixtureClick
}: {
  form: string | null | undefined
  formDetails?: FormTeamOut[]
  onFixtureClick?: (fixtureId: number) => void
}) {
  // If we have formDetails, use it (accurate W/D/L + opponent info)
  if (formDetails && formDetails.length > 0) {
    // formDetails is ordered most recent first, reverse so most recent is on right
    const reversed = [...formDetails].slice(0, 5).reverse()

    return (
      <div className="form-badges">
        {reversed.map((detail, idx) => {
          const venue = detail.is_home ? 'H' : 'A'
          const tooltip = `${detail.opponent.name} (${venue})\n${detail.score_for}-${detail.score_against}`
          const isMostRecent = idx === reversed.length - 1
          const isClickable = Boolean(onFixtureClick && detail.fixture_id)

          const handleClick = (e: React.MouseEvent) => {
            e.stopPropagation()
            if (onFixtureClick && detail.fixture_id) {
              onFixtureClick(detail.fixture_id)
            }
          }

          return (
            <span
              key={idx}
              className={`form-badge-mini ${detail.result.toLowerCase()} ${isMostRecent ? 'most-recent' : ''} ${isClickable ? 'clickable' : ''}`}
              title={tooltip}
              onClick={isClickable ? handleClick : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={isClickable ? (e) => e.key === 'Enter' && handleClick(e as unknown as React.MouseEvent) : undefined}
            >
              {detail.result}
            </span>
          )
        })}
      </div>
    )
  }

  // Fallback to form string
  if (!form) return null

  const formChars = form.split('').slice(-5).reverse()

  return (
    <div className="form-badges">
      {formChars.map((char, idx) => {
        const resultLabels: Record<string, string> = { W: 'Win', D: 'Draw', L: 'Loss' }
        const tooltip = resultLabels[char.toUpperCase()] || char
        const isMostRecent = idx === formChars.length - 1

        return (
          <span
            key={idx}
            className={`form-badge-mini ${char.toLowerCase()} ${isMostRecent ? 'most-recent' : ''}`}
            title={tooltip}
          >
            {char}
          </span>
        )
      })}
    </div>
  )
}

export function LeagueTable({
  standings,
  allTeamsForm,
  nextFixtures,
  onTeamClick,
  onFixtureClick,
  highlightTeamId,
  compact = false,
}: LeagueTableProps) {
  if (!standings || standings.length === 0) {
    return <div className="league-table-empty">No standings available</div>
  }

  // Find next opponent for each team
  const getNextOpponent = (teamId: number): { team: TeamLite; isHome: boolean } | null => {
    if (!nextFixtures) return null
    const fixture = nextFixtures.find(f =>
      f.home_team.id === teamId || f.away_team.id === teamId
    )
    if (!fixture) return null
    const isHome = fixture.home_team.id === teamId
    return {
      team: isHome ? fixture.away_team : fixture.home_team,
      isHome,
    }
  }

  return (
    <div className={`league-table-wrapper ${compact ? 'compact' : ''}`}>
      <table className="team-standings-table">
        <thead>
          <tr>
            <th className="col-pos">#</th>
            <th className="col-team">Team</th>
            <th className="col-pl">PL</th>
            <th className="col-w">W</th>
            <th className="col-d">D</th>
            <th className="col-l">L</th>
            <th className="col-gf">+/-</th>
            <th className="col-gd">GD</th>
            <th className="col-pts">PTS</th>
            <th className="col-form">Form</th>
            {!compact && <th className="col-next">Next</th>}
          </tr>
        </thead>
        <tbody>
          {standings.map(row => {
            const nextOpp = !compact ? getNextOpponent(row.team_id) : null
            const formDetails = allTeamsForm?.[row.team_id]

            return (
              <tr
                key={row.team_id}
                className={highlightTeamId === row.team_id ? 'highlighted' : ''}
                onClick={() => onTeamClick?.({
                  id: row.team_id,
                  name: row.team_name,
                  crest_url: row.team_crest
                })}
              >
                <td className="col-pos">{row.position}</td>
                <td className="col-team">
                  <div className="table-team-cell">
                    {row.team_crest && <img src={row.team_crest} alt="" className="table-team-logo" />}
                    <span className="table-team-name">{row.team_name}</span>
                  </div>
                </td>
                <td className="col-pl">{row.played}</td>
                <td className="col-w">{row.wins}</td>
                <td className="col-d">{row.draws}</td>
                <td className="col-l">{row.losses}</td>
                <td className="col-gf">{row.goals_for}-{row.goals_against}</td>
                <td className="col-gd">{row.goal_difference > 0 ? `+${row.goal_difference}` : row.goal_difference}</td>
                <td className="col-pts">{row.points}</td>
                <td className="col-form">
                  <FormBadges
                    form={row.form}
                    formDetails={formDetails}
                    onFixtureClick={onFixtureClick}
                  />
                </td>
                {!compact && (
                  <td className="col-next">
                    {nextOpp && (
                      <div className="next-opp-cell" title={`${nextOpp.team.name} (${nextOpp.isHome ? 'H' : 'A'})`}>
                        {nextOpp.team.crest_url ? (
                          <img src={nextOpp.team.crest_url} alt="" className="next-opp-logo" />
                        ) : (
                          <span className="next-opp-placeholder">{nextOpp.team.name.charAt(0)}</span>
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default LeagueTable
